import { useEffect, useMemo, useState } from "react";
import { Polyline } from "@vis.gl/react-google-maps";
import { IconButton, Tooltip } from "@mui/material";
import ManageSearch from "@mui/icons-material/ManageSearch";
import Map from "features/maps/Map";
import MapSearchField from "features/maps/MapSearchField";
import { Company } from "models/Company";
import PlaceMarker from "./PlaceMarker";
import AddressFromOrderDialog from "../dialogs/AddressFromOrderDialog";
import MapToolbar from "features/maps/MapToolbar";
import CompanyMarker from "./CompanyMarker";

type CompaniesMapProps = {
  companies: Company[];
};

const CompaniesMap: React.FC<CompaniesMapProps> = ({ companies }) => {
  const [query, setQuery] = useState("");
  const [selectedOffice, setSelectedOffice] = useState<Company | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.Place | null>(null);
  const [searchResults, setSearchResults] = useState<google.maps.places.Place[]>([]);
  const [currentRoute, setCurrentRoute] = useState<google.maps.routes.Route | null>(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);

  /* Compute a flat list of all companies and their offices, so that we can display them all on the map. */
  const offices = useMemo(() => {
    return companies.flatMap((company) => [
      company,
      ...company.offices.map((office) => ({ ...company, ...office })),
    ]);
  }, [companies]);

  /* When the list of companies changes, if the selected company is no longer in the list, clear the selection. */
  useEffect(() => {
    if (!selectedOffice) return;

    if (!offices.some((company) => selectedOffice.id === company.id)) {
      setSelectedOffice(null);
    }
  }, [offices, selectedOffice]);

  /* When search results change, if there is only one result, select it automatically. Otherwise, clear the selection. */
  useEffect(() => {
    if (searchResults.length === 1) {
      setSelectedPlace(searchResults[0]);
    } else {
      setSelectedPlace(null);
    }
  }, [searchResults]);

  /* When the selected company or place changes, compute a route between them. */
  useEffect(() => {
    setCurrentRoute(null);

    if (!selectedOffice || !selectedPlace) return;

    google.maps.routes.Route.computeRoutes({
      origin: { lat: selectedOffice.latitude, lng: selectedOffice.longitude },
      destination: selectedPlace.location!,
      fields: ["*"],
    }).then(({ routes }) => {
      setCurrentRoute(routes?.[0] || null);
    });
  }, [selectedOffice, selectedPlace]);

  return (
    <Map>
      <MapToolbar>
        <MapSearchField value={query} onChange={setQuery} onResults={setSearchResults} />

        <Tooltip title="Hämta adress från order" arrow>
          <IconButton variant="contained" color="paper" onClick={() => setShowOrderDialog(true)}>
            <ManageSearch />
          </IconButton>
        </Tooltip>
      </MapToolbar>

      {currentRoute &&
        currentRoute
          .createPolylines()
          .map((polyline, index) => (
            <Polyline
              key={index}
              path={polyline.getPath()}
              strokeWeight={7}
              strokeColor="#2f6ae1"
              strokeOpacity={0.8}
            />
          ))}

      {offices
        ?.filter((company) => company.latitude && company.longitude)
        .map((company) => (
          <CompanyMarker
            key={company.id}
            company={company}
            route={currentRoute}
            isSelected={company.id === selectedOffice?.id}
            onSelect={() => setSelectedOffice(company)}
            onClose={() => setSelectedOffice(null)}
          />
        ))}

      {searchResults.map((place) => {
        return (
          <PlaceMarker
            key={place.id}
            place={place}
            selected={place.id === selectedPlace?.id}
            onSelect={() => setSelectedPlace(place)}
          />
        );
      })}

      {showOrderDialog && (
        <AddressFromOrderDialog
          onClose={() => setShowOrderDialog(false)}
          onConfirm={(address) => {
            setQuery(address);
            setShowOrderDialog(false);
          }}
        />
      )}
    </Map>
  );
};

export default CompaniesMap;
